import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Lock, CheckCircle2, Sparkles, Loader2, Target, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const SUBJECTS = [
  { key: 'reading', label: 'Reading', gradient: 'from-emerald-500 to-teal-500' },
  { key: 'math', label: 'Math', gradient: 'from-orange-500 to-amber-500' },
];

const SkillMapPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [subject, setSubject] = useState('math');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    api
      .get(`/skills/map/${childId}?subject=${subject}`)
      .then((r) => { if (!ignore) setData(r.data); })
      .catch(() => { if (!ignore) toast.error('Could not load skill map'); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [childId, subject, api]);

  const startSkill = (skill) => {
    // Route to adaptive AI curriculum with the skill label as focus
    navigate(`/ai-curriculum/${childId}/${subject}?skill_focus=${encodeURIComponent(skill.label)}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/learn/${childId}`)} data-testid="skillmap-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-500" /> Skill Map
            </h1>
            <p className="text-xs text-slate-500">Master each skill to unlock the next one — IXL-style progression.</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="flex gap-2 flex-wrap" data-testid="skillmap-subject-tabs">
          {SUBJECTS.map((s) => (
            <Button
              key={s.key}
              variant={subject === s.key ? 'default' : 'outline'}
              className={subject === s.key ? `bg-gradient-to-r ${s.gradient} text-white border-0` : ''}
              onClick={() => setSubject(s.key)}
              data-testid={`skillmap-subject-${s.key}`}
            >
              {s.label}
            </Button>
          ))}
        </div>

        {loading && (
          <div className="py-24 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        )}

        {!loading && data?.recommended_next && (
          <Card className="border-2 border-orange-300 bg-orange-50">
            <CardContent className="p-5 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-orange-500 mt-1" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-orange-600">Recommended next</p>
                  <p className="font-bold text-slate-800">{data.recommended_next.label}</p>
                  <p className="text-xs text-slate-500">Current mastery: {data.recommended_next.mastery}% · target {data.recommended_next.target}%</p>
                </div>
              </div>
              <Button
                className="bg-orange-500 hover:bg-orange-600"
                onClick={() => startSkill(data.recommended_next)}
                data-testid="skillmap-recommended-start"
              >
                <TrendingUp className="w-4 h-4 mr-1" /> Practice this
              </Button>
            </CardContent>
          </Card>
        )}

        {!loading && (data?.bands || []).map((b) => (
          <Card key={b.band} className="border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase tracking-widest text-slate-500">Band · {b.band}</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3">
              {b.skills.map((s) => {
                const mastered = s.mastery >= (s.target || 80);
                return (
                  <div
                    key={s.id}
                    className={`rounded-xl border-2 p-4 transition-all ${
                      !s.unlocked
                        ? 'border-slate-200 bg-slate-50 opacity-60'
                        : mastered
                        ? 'border-emerald-400 bg-emerald-50'
                        : 'border-slate-300 hover:border-orange-400 hover:bg-orange-50/40'
                    }`}
                    data-testid={`skill-card-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-xs text-slate-400 font-mono">{s.id}</p>
                        <p className="font-bold text-slate-800">{s.label}</p>
                      </div>
                      {!s.unlocked ? (
                        <Lock className="w-5 h-5 text-slate-400" />
                      ) : mastered ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <Badge variant="outline" className="text-xs">Ready</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={s.mastery} className="h-2 flex-1" />
                      <span className="text-xs font-bold text-slate-600 tabular-nums">{s.mastery}%</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Target {s.target}%</p>
                    {s.unlocked && !mastered && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 w-full"
                        onClick={() => startSkill(s)}
                        data-testid={`skill-start-${s.id}`}
                      >
                        Practice
                      </Button>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </main>
    </div>
  );
};

export default SkillMapPage;
